import {
  SAVE_OWNER_KEY, fetchCloudRev, fetchCloudSaveRetrying, readLocalBlob,
  readSyncedRev, writeLocalFromCloud, writeSyncedRev, clearSyncedRev,
  type CloudSave,
} from './saveTransport'
import { STORAGE_KEY } from '@/store/storageKeys'
import { preserveMonotonicKills, type MergeableSave } from '@/services/game/saveMerge'

/**
 *  DECIDE WHICH SAVE IS REAL, BEFORE THE GAME EXISTS.
 *
 *  Reported twice, the second time after 0135 had shipped: *"the save is still
 *  95 on one device and 97 on the other"*. The old resolution ran AFTER the
 *  store had been built from the local save, so its only way to apply a cloud
 *  save was to write localStorage and reload the page — capped at one reload
 *  per tab session, which an installed PWA never clears on resume. A phone
 *  that spent its reload was stuck on the old character with saving paused,
 *  permanently, and the game told it to "close and reopen" which did not help.
 *
 *  This module runs from `main.tsx` before `@/store/store` is imported at all.
 *  By the time the store evaluates `loadPersistedState()`, localStorage already
 *  holds the winning save. No reload. No budget. No dead end. It costs a
 *  network round trip on boot, which is what the loading screen is for.
 *
 *  ── THE RULE ─────────────────────────────────────────────────────────────
 *  Clock-free, because every clock in the old design belonged to a client:
 *
 *    cloud rev  >  our synced rev   someone else wrote. CLOUD WINS.
 *    cloud rev === our synced rev   nobody wrote; our local may hold offline
 *                                   progress. LOCAL WINS, and we push it.
 *    cloud rev  <  our synced rev   impossible unless the save was deleted and
 *                                   recreated. Treat as cloud (it is what the
 *                                   account actually has).
 *    no synced rev at all           the save predates 0136. Fall back to the
 *                                   old timestamp comparison ONCE, then adopt
 *                                   a revision and never need clocks again.
 */

export type BootSaveStatus =
  /** Local save kept; it is the authority (or there is no backend). */
  | 'local'
  /** Cloud save pulled down; the store will build from it. */
  | 'cloud'
  /** This account has no cloud save and no usable local one — new character. */
  | 'fresh'
  /** Could not reach the cloud. Local save kept, and pushes must stay blocked. */
  | 'unreachable'
  /** The local save belonged to a different account and was set aside. */
  | 'switched-account'

export interface BootSaveResult {
  status: BootSaveStatus
  /** The revision the device is now synced at, where known. */
  rev: number | null
  /** One line, for the loading screen and the console. Player-readable. */
  detail: string
}

/**
 *  Apply a cloud save, keeping kills this device recorded that the cloud has
 *  not seen.
 *
 *  The old reload-based hydrate did this and it must not be lost in the move:
 *  a pull replaces the WHOLE blob, so bestiary progress earned offline on this
 *  device would vanish every time another device wrote. `preserveMonotonicKills`
 *  takes the max per enemy, and refuses outright when the two blobs are
 *  different characters.
 */
function applyCloud(cloud: CloudSave, local: Record<string, unknown> | null): void {
  try {
    const { recovered, skipped } = preserveMonotonicKills(
      cloud.data as MergeableSave,
      local as MergeableSave | null,
    )
    if (recovered > 0) console.warn(`[boot] kept ${recovered} local kill(s) the cloud save had not seen`)
    else if (skipped === 'different-character') console.info('[boot] cloud save is a different character - replacing cleanly')
  } catch {
    // A merge must never block a boot. Falling through writes the cloud save
    // unmerged, which is the behaviour that shipped for months.
  }
  writeLocalFromCloud(cloud)
}

/** Local `lastSeenAt`, read straight off the blob. Only used for the one-time
 *  pre-0136 fallback below — nothing else in this file looks at a clock. */
function localLastSeenAt(blob: Record<string, unknown> | null): string | null {
  const player = (blob?.player ?? null) as { data?: { lastSeenAt?: string } } | null
  return player?.data?.lastSeenAt ?? null
}

function cloudLastSeenAt(data: Record<string, unknown>): string | null {
  const player = (data.player ?? null) as { data?: { lastSeenAt?: string } } | null
  return player?.data?.lastSeenAt ?? null
}

/**
 *  Does this blob actually contain a character?
 *
 *  An empty shell must never win a comparison against a real cloud save.
 *
 *  THE FIELD IS `username`. The first version read `name`, which the Player
 *  type does not have (`createPlayer` sets `username`), so this returned false
 *  for every save in existence — and that is not a cosmetic bug: the
 *  same-revision branch below is guarded on it, so a device whose local save
 *  was already authoritative would instead fetch the cloud and overwrite its
 *  own un-pushed offline progress. Caught by a driver run noticing the
 *  same-rev case "wrongly fetched the cloud".
 */
function hasCharacter(blob: Record<string, unknown> | null): boolean {
  const player = (blob?.player ?? null) as { data?: { username?: string } | null } | null
  return !!player?.data?.username
}

/**
 *  Resolve the save for a signed-in account.
 *
 *  Never throws: a boot that cannot decide must still start the game on
 *  whatever is local, with pushing disabled, rather than hang on a splash
 *  screen. Returning 'unreachable' is how it says so.
 */
export async function resolveBootSave(userId: string): Promise<BootSaveResult> {
  const owner = (() => {
    try { return window.localStorage.getItem(SAVE_OWNER_KEY) } catch { return null }
  })()
  const localBlob = readLocalBlob()

  // ── a DIFFERENT account's save is sitting on this device ──
  // Newest-wins is meaningless here: the two saves are different people. This
  // is the "signed into B but still see A's character" case, and getting it
  // wrong once cloned one player's character into someone else's account.
  if (owner && owner !== userId) {
    let cloud = null
    try {
      cloud = await fetchCloudSaveRetrying(userId)
    } catch {
      // Cannot tell whether this account has a save. Do NOT adopt the other
      // account's character and do NOT claim ownership — that is the path that
      // uploads the wrong save. Start clean for this boot.
      return { status: 'unreachable', rev: null, detail: 'Could not reach the server' }
    }
    // Keep the outgoing save recoverable — a tester lost a level 88 this way.
    try {
      const outgoing = window.localStorage.getItem(STORAGE_KEY)
      if (outgoing) window.localStorage.setItem(`elderglen-save-backup-${owner}`, outgoing)
    } catch { /* storage full — proceed */ }

    try { window.localStorage.setItem(SAVE_OWNER_KEY, userId) } catch { /* ignore */ }
    if (cloud) {
      applyCloud(cloud, null)
      return { status: 'cloud', rev: cloud.rev, detail: 'Loaded your character' }
    }
    // This account has no save, and the local one is stamped with someone
    // else's id. It is not ours to adopt.
    try { window.localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    clearSyncedRev()
    return { status: 'switched-account', rev: 0, detail: 'New character' }
  }

  // ── same account (or first boot on this device) ──
  const syncedRev = readSyncedRev()
  const cloudRev = await fetchCloudRev()

  if (cloudRev === null) {
    // Asked and could not find out. Keep local; the caller blocks pushes so a
    // stale device cannot overwrite a cloud save it never managed to read.
    return { status: 'unreachable', rev: null, detail: 'Could not reach the server' }
  }

  // The account has no cloud save at all.
  if (cloudRev === 0) {
    try { window.localStorage.setItem(SAVE_OWNER_KEY, userId) } catch { /* ignore */ }
    // A device-local character with nothing in the cloud is the guest who just
    // signed up: keep it and let the first push create the row.
    clearSyncedRev()
    return hasCharacter(localBlob)
      ? { status: 'local', rev: 0, detail: 'Using this device’s save' }
      : { status: 'fresh', rev: 0, detail: 'New character' }
  }

  // Nobody has written since we synced — our local copy is at least as new,
  // and may hold offline progress the cloud has not seen.
  if (syncedRev !== null && cloudRev === syncedRev && hasCharacter(localBlob)) {
    try { window.localStorage.setItem(SAVE_OWNER_KEY, userId) } catch { /* ignore */ }
    return { status: 'local', rev: cloudRev, detail: 'Your save is up to date' }
  }

  // Either the cloud moved, or we have never recorded a revision. Fetch it.
  let cloud
  try {
    cloud = await fetchCloudSaveRetrying(userId)
  } catch {
    return { status: 'unreachable', rev: null, detail: 'Could not reach the server' }
  }
  if (!cloud) {
    // save_rev said there was a row and the fetch says there isn't. Racing a
    // delete (account reset). Treat the account as empty rather than guessing.
    clearSyncedRev()
    return { status: 'fresh', rev: 0, detail: 'New character' }
  }

  try { window.localStorage.setItem(SAVE_OWNER_KEY, userId) } catch { /* ignore */ }

  // ── the one-time pre-0136 path ──
  // No recorded revision, so we cannot tell whether the cloud moved. This is
  // the only place a clock is consulted, and only until the first push stamps
  // a revision. Prefer the cloud on a tie: the cloud is shared, a stale local
  // copy is not, and the failure modes are not symmetrical — pulling costs the
  // player an offline session, pushing costs them everything the other device
  // did.
  if (syncedRev === null) {
    const localTs = localLastSeenAt(localBlob)
    const cloudTs = cloudLastSeenAt(cloud.data as Record<string, unknown>)
    const localIsNewer =
      hasCharacter(localBlob) && !!localTs && (!cloudTs || Date.parse(localTs) > Date.parse(cloudTs))
    if (localIsNewer) {
      // Keep local, but adopt the cloud's revision so the very next push is a
      // correct compare-and-swap rather than another guess.
      writeSyncedRev(cloud.rev)
      return { status: 'local', rev: cloud.rev, detail: 'Using this device’s save' }
    }
    applyCloud(cloud, localBlob)
    return { status: 'cloud', rev: cloud.rev, detail: 'Loaded your character' }
  }

  // The cloud moved. This is the 95-vs-97 case, and it is now just a write.
  applyCloud(cloud, localBlob)
  return { status: 'cloud', rev: cloud.rev, detail: 'Caught up from your other device' }
}
